import { IClientResponse } from '@orbis-systems/omni-sdk-ts/dist/types/apis/core/client';
import { find, flatMap, isArray } from 'lodash';
import { inject, singleton } from 'tsyringe';
import { CachedResponseApi } from '../BaseApi';
import { ICache } from '../Cache';
import {
    AccountStatus,
    CorporateActionsType,
    OrderFilterType,
    OverExposedType,
    PositionType,
    UserType,
} from '../constants/lookups';
import { ApiInjectToken, MiddlewareInjectToken } from '../constants/tokens';
import { BranchInfo, MiddlewareConfig, Notification, Order, OrderWithExtraInfo, Sdks } from '../types';
import {
    Account,
    AccountWithExtraInfo,
    OverExposedAccount,
    PositionAccount,
    PositionAccountWithExtraInfo,
} from '../types/Account.type';
import { CorporateAction, CorporateActionWithExtraInfo } from '../types/CorporateAction.type';
import { Model } from '../types/Model.type';
import { Portfolio, PortfolioWithExtraInfo } from '../types/Portfolio.type';
import { User } from '../types/User.type';
import {
    formatCorporateActions,
    formatOrders,
    formatOverExposedAccounts,
    formatPortfolio,
    formatPositionAccounts,
} from '../utils/dataUtils';
import { divide, sumBy } from '../utils/numberUtils';
import { IGroupApi, IModelApi, IUserApi } from './interfaces';
import { IBranchApi } from './interfaces/IBranchApi';

@singleton()
export class BranchApi extends CachedResponseApi implements IBranchApi {
    constructor(
        @inject(MiddlewareInjectToken.Sdks) sdks: Sdks,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
        @inject(MiddlewareInjectToken.Cache) cache: ICache,
        @inject(ApiInjectToken.ModelApi) private modelApi: IModelApi,
        @inject(ApiInjectToken.GroupApi) private groupApi: IGroupApi,
        @inject(ApiInjectToken.UserApi) protected userApi: IUserApi,
    ) {
        super(sdks, config, cache);
    }

    async getInfo(): Promise<BranchInfo> {
        const data = await Promise.all([this.getAccounts(), this.modelApi.getModels(), this.groupApi.getGroups()]);

        const accounts = data[0];
        const models = data[1];
        const groups = data[2];

        return {
            balance: sumBy(accounts, (account) => account.balance),
            cash: sumBy(accounts, (account) => account.cashOnAccount),
            monthlyCashflow: divide(
                sumBy(accounts, (account) => account.totalDividendAmount),
                12,
            ),
            portfolioValue: sumBy(accounts, (account) => account.markToMarket),
            totalAccounts: accounts.length,
            totalModels: models.length,
            totalGroups: groups.length,
        };
    }

    async getUsers(): Promise<User[]> {
        const { data } = await this.sdks.orbis.get('v2/advisory/users/list');
        return (data as User[]).filter((user) => user.userType === UserType.Client);
    }

    async getAccounts(): Promise<AccountWithExtraInfo[]> {
        const data = await Promise.all([this.userApi.getUserInfo(), this.sdks.orbis.get('v1/branch/rtbs')]);

        const accounts = data[0].accountList;

        return accounts
            .filter((account) => account.status === AccountStatus.Active)
            .map((account) => {
                const {
                    balance = 0,
                    cashOnAccount = 0,
                    markToMarket = 0,
                    sodCashBalance = 0,
                    sodMarginBalance = 0,
                    sodShortBalance = 0,
                    totalDividendAmount = 0,
                    totalDividendYield = 0,
                } = find(data[1].data, (item) => item.accountId === account.accountId) || {};

                return {
                    ...account,
                    balance,
                    cashOnAccount,
                    markToMarket,
                    sodCashBalance,
                    sodMarginBalance,
                    sodShortBalance,
                    totalDividendAmount,
                    totalDividendYield,
                };
            });
    }

    async getAccount(accountId: number): Promise<AccountWithExtraInfo | null> {
        const accounts = await this.getAccounts();

        const account = accounts.find((account) => account.accountId === accountId);

        if (!account) {
            return null;
        }

        return account;
    }

    async getInventoryAccounts(): Promise<Account[]> {
        return (await this.sdks.orbis.get('v1/branch/inventory')).data;
    }

    async getPositionAccounts(symbol: string): Promise<PositionAccountWithExtraInfo[]> {
        const data = await Promise.all([
            this.sdks.orbis.get('user/position/search', undefined, {
                params: { symbols: symbol, loadQuotes: true },
            }),
            this.getPortfolio(false),
        ]);

        const positionAccounts = data[0].data as PositionAccount[];
        const branchPortfolio = data[1];

        return formatPositionAccounts(symbol, positionAccounts, branchPortfolio);
    }

    async getPortfolio(combined = true, symbol?: string, positionType?: PositionType): Promise<PortfolioWithExtraInfo> {
        const data = await Promise.all([
            this.getAccounts(),
            this.getInventoryAccounts(),
            this.sdks.orbis.get('v1/branch/rtb'),
            this.sdks.orbis.get('v1/branch/portfolio', undefined, {
                params: {
                    combined,
                    loadQuotes: true,
                    loadIndustries: true,
                    loadRtb: true,
                    marketCapBreakdown: true,
                },
            }),
        ]);

        const accounts = data[0];
        const inventoryAccounts = data[1];
        const balance = data[2].data.balance || 0;
        const portfolio = data[3].data as Portfolio;

        return formatPortfolio(portfolio, accounts, inventoryAccounts, balance, { symbol, positionType: positionType });
    }

    async getOrders(
        filterType: OrderFilterType | OrderFilterType[] = OrderFilterType.Latest,
        symbol?: string,
    ): Promise<OrderWithExtraInfo[]> {
        const data = await Promise.all([
            this.sdks.orbis.get('v2/advisory/models'),
            this.userApi.getUserInfo(),
            this.getInventoryAccounts(),
            ...(isArray(filterType) ? filterType : [filterType]).map((filter) => {
                return this.sdks.orbis.get('orders/branch', undefined, {
                    urlQuery: filter,
                    params: { loadQuotes: true, symbol },
                });
            }),
        ]);

        const models = (data[0] as IClientResponse).data as Model[];
        const accounts = data[1].accountList;
        const inventoryAccounts = data[2] as Account[];
        const orders = flatMap(data.slice(3) as IClientResponse[], (item) => item.data) as Order[];

        return formatOrders(orders, models, accounts, inventoryAccounts[0]);
    }

    async getOverExposedAccounts(threshold: number, type: OverExposedType): Promise<OverExposedAccount[]> {
        const { data } = await this.sdks.orbis.get('v2/advisory/analytics/overexposure', undefined, {
            urlQuery: [type, 'branch', 'all'],
            params: { threshold },
        });
        const accounts = await this.getAccounts();

        return formatOverExposedAccounts(data, type, accounts);
    }

    async getCorporateActions(
        symbols: string[],
        dateFrom: string,
        dateTo: string,
        types?: CorporateActionsType,
    ): Promise<CorporateActionWithExtraInfo[]> {
        const positions = (await this.getPortfolio()).entries;
        const actions = (
            await this.sdks.orbis.get('research/actions/search', undefined, {
                params: {
                    symbol: symbols?.join(','),
                    dateFrom,
                    dateTo,
                    types,
                },
            })
        ).data as CorporateAction[];

        return formatCorporateActions(actions, positions);
    }

    async getNotifications(): Promise<Notification[]> {
        const response = await this.sdks.middleware.get('messages');
        return response.data;
    }
}
